import BigNumber from 'bignumber.js';
import {
  BundlePaymentPlan,
  ISubscriptionsItem,
  Web3Address,
} from 'multirpc-sdk';
import { createSelector } from '@reduxjs/toolkit';

import { AccountPermission } from 'modules/permissions/constants';
import { DEFAULT_SELECTED_RECURRING_USD_AMOUNT } from 'modules/payments/const';
import { ECurrency, IAmount } from 'modules/payments/types';
import { RootState } from 'store';
import { ZERO } from 'modules/common/constants/const';
import { fetchBundlePaymentPlans } from 'domains/account/actions/bundles/fetchBundlePaymentPlans';
import { fetchMyBundles } from 'modules/billing/actions/fetchMyBundles';
import { fetchMySubscriptions } from 'domains/account/actions/subscriptions/fetchMySubscriptions';
import { fetchUSDSubscriptionPrices } from 'domains/account/actions/usdTopUp/fetchUSDSubscriptionPrices';
import { isDealPlan } from 'domains/account/utils/isDealPlan';
import { isPackagePlan } from 'domains/account/utils/isPackagePlan';
import { selectAccountPermissions } from 'modules/account/store/selectors';
import { selectIsFreemium } from 'modules/accountStatus/store/selectors';
import { selectIsWeb3OAuth } from 'modules/user/store/selectors';
import { selectPAYGBalance } from 'modules/billing/store/selectors';

import { getPremiumActivationThreshold } from '../utils/getPremiumActivationThreshold';

export const selectTopUpOrigin = (state: RootState) =>
  state.accountTopUp.topUpOrigin;

export const selectBundlePaymentPlansState = createSelector(
  fetchBundlePaymentPlans.select(),
  state => state,
);

export const selectBundlePaymentPlans = createSelector(
  selectBundlePaymentPlansState,
  ({ data = [] }) =>
    data.map<BundlePaymentPlan>(bundle => ({
      ...bundle,
      price: {
        ...bundle.price,
        amount: (Number(bundle.price.amount) || 0).toString(),
      },
    })),
);

export const selectPaidBundlePaymentPlans = createSelector(
  selectBundlePaymentPlans,
  plans => plans.filter(plan => plan.bundle.type === 'PAID'),
);

export const selectDealPaymentPlans = createSelector(
  selectPaidBundlePaymentPlans,
  plans =>
    plans
      .filter(isDealPlan)
      .sort((a, b) => Number(a.price.amount) - Number(b.price.amount)),
);

export const selectDealAmounts = createSelector(selectDealPaymentPlans, plans =>
  plans.map<IAmount>(plan => ({
    id: plan.bundle.price_id,
    value: Number(plan.price.amount),
    currency: ECurrency.USD,
  })),
);

export const selectBundlePaymentPlansInitLoading = createSelector(
  selectBundlePaymentPlansState,
  ({ data, isLoading }) => isLoading && typeof data === 'undefined',
);

export const selectBundlePaymentPlanByPriceId = createSelector(
  selectPaidBundlePaymentPlans,
  (_state: RootState, priceId?: string) => priceId,
  (bundles, priceId) =>
    bundles.find(({ bundle }) => bundle.price_id === priceId),
);

export const selectMyBundlesState = createSelector(
  (state: RootState, group: Web3Address | undefined) =>
    fetchMyBundles.select({ group })(state),
  state => state,
);

export const selectMyBundles = createSelector(
  selectMyBundlesState,
  ({ data }) => data || [],
);

export const selectMyCurrentBundle = createSelector(
  selectMyBundles,
  (bundles): ISubscriptionsItem | undefined => bundles[0],
);

export const selectMySubscriptionsState = createSelector(
  fetchMySubscriptions.select({}),
  state => state,
);

export const selectMySubscriptionsInitLoading = createSelector(
  selectMySubscriptionsState,
  ({ data, isLoading }) => isLoading && typeof data === 'undefined',
);

export const selectMySubscriptions = createSelector(
  selectMySubscriptionsState,
  ({ data }) => data?.items || [],
);

export const selectMyRecurringPayments = createSelector(
  selectMyBundles,
  selectMySubscriptions,
  (bundles, subscriptions) =>
    [...subscriptions, ...bundles].sort(
      (a, b) => Number(a.currentPeriodEnd) - Number(b.currentPeriodEnd),
    ),
);

export const selectMyRecurringPaymentsWithoutPackage = createSelector(
  selectMyRecurringPayments,
  selectPaidBundlePaymentPlans,
  (payments, bundlePaymentPlans) => {
    return payments.filter(payment => {
      const currentPlan = bundlePaymentPlans.find(plan => {
        return plan.bundle.price_id === payment.productPriceId;
      });

      return !isPackagePlan(currentPlan);
    });
  },
);

export const selectAllRecurringPaymentsAmount = createSelector(
  selectMyRecurringPayments,
  payments =>
    payments
      .reduce((amount, payment) => amount.plus(payment.amount), ZERO)
      .toString(),
);

export const selectHasRecurringPayments = createSelector(
  selectMyRecurringPayments,
  payments => payments.length > 0,
);

export const selectHasActiveDeal = createSelector(
  selectMyBundles,
  selectPaidBundlePaymentPlans,
  (myBundles, bundlePaymentPlans) => {
    const activeDeals = myBundles.filter(bundle => {
      const currentPlan = bundlePaymentPlans.find(plan => {
        return plan.bundle.price_id === bundle.productPriceId;
      });

      return isDealPlan(currentPlan);
    });

    return activeDeals.length > 0;
  },
);

export const selectHighestRankDealPriceData = createSelector(
  selectHasActiveDeal,
  selectPaidBundlePaymentPlans,
  (hasActiveDeal, bundlePaymentPlans) => {
    if (hasActiveDeal && bundlePaymentPlans.length) {
      const mappedData = bundlePaymentPlans.map(
        planData => +(planData?.price?.amount || 0),
      );

      return Math.max(...mappedData);
    }

    return undefined;
  },
);

/**
 * @deprecated use `useDealPaymentPlans` hook instead
 */
export const selectIsHighestDealPurchased = createSelector(
  selectHighestRankDealPriceData,
  selectMyCurrentBundle,
  (highestDealPrice, myCurrentBundlePlan) => {
    if (highestDealPrice && myCurrentBundlePlan) {
      return +myCurrentBundlePlan.amount >= highestDealPrice;
    }

    return false;
  },
);

export const selectUSDSubscriptionPricesState = createSelector(
  fetchUSDSubscriptionPrices.select(undefined as unknown as never),
  state => state,
);

export const selectUSDSubscriptionPricesFetching = createSelector(
  selectUSDSubscriptionPricesState,
  ({ data, isLoading }) => isLoading && typeof data !== 'undefined',
);

export const selectUSDSubscruptionPricesLoading = createSelector(
  selectUSDSubscriptionPricesState,
  ({ isLoading }) => isLoading,
);

export const selectUSDSubscruptionPrices = createSelector(
  selectUSDSubscriptionPricesState,
  ({ data = [] }) =>
    [...data].sort((a, b) => Number(a.amount) - Number(b.amount)),
);

export const selectUSDSubscriptionAmounts = createSelector(
  selectUSDSubscruptionPrices,
  prices =>
    prices
      // Filtering unnecessary price to fit the design.
      // Presented on stage environment only.
      .filter(price => price.amount !== '12.00')
      .map<IAmount>(price => ({
        id: price.id,
        value: Number(price.amount),
        currency: ECurrency.USD,
      })),
);

export const selectDefaultUSDSubscriptionAmountID = createSelector(
  selectUSDSubscriptionAmounts,
  amounts =>
    amounts.find(
      amount => amount.value === DEFAULT_SELECTED_RECURRING_USD_AMOUNT,
    )?.id,
);

const freeToPremiumThreshold = getPremiumActivationThreshold();

export const selectHasFreemiumToPremiumTransition = createSelector(
  (state: RootState, _token: string, group: Web3Address | undefined) =>
    selectPAYGBalance(state, group).credits,
  selectMyCurrentBundle,
  (state: RootState, userEndpointToken: string) =>
    selectIsFreemium(state, { userEndpointToken }),
  (state: RootState) => selectIsWeb3OAuth(state),
  // eslint-disable-next-line max-params
  (balance, myBundle, hasFreemium, isWeb3UserWithEmailBound) => {
    const hasEnoughBalance = new BigNumber(balance).isGreaterThanOrEqualTo(
      freeToPremiumThreshold,
    );

    const hasBundle = Boolean(myBundle);
    const shouldBePremium = hasEnoughBalance || hasBundle;
    const hasNoEmailBound = !isWeb3UserWithEmailBound;

    return hasNoEmailBound && hasFreemium && shouldBePremium;
  },
);

export const selectHasAccountStatusTransition = createSelector(
  selectHasFreemiumToPremiumTransition,
  (state: RootState) => selectAccountPermissions(state),
  (hasFreeToPremiumTransition, accountPermissions) => {
    const hasAccess = accountPermissions.includes(
      AccountPermission.StatusTransition,
    );

    return hasAccess && hasFreeToPremiumTransition;
  },
);
